// src/components/inventory/PartRequirements.jsx
import React, { useState, useEffect } from 'react';
import {partOrderService, vendorService} from '../../services/api';
import { toast } from 'react-toastify';
import SupplyChart from "./SupplyChart";

const SupplyOrder = () => {
	const [requirements, setRequirements] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [sortConfig, setSortConfig] = useState({
		                                             key: 'total_quantity_needed',
		                                             direction: 'descending'
	                                             });

	// For filtering
	const [searchTerm, setSearchTerm] = useState('');
	const [showCreateOrderDialog, setShowCreateOrderDialog] = useState(false);
	const [selectedVendor, setSelectedVendor] = useState(null);
	const [vendors, setVendors] = useState([]);
	const [orderSuccess, setOrderSuccess] = useState(false);
	const [isCreatingOrder, setIsCreatingOrder] = useState(false);

	useEffect(() => {
		fetchPartRequirements();
		fetchVendors();
	}, []);

	const fetchPartRequirements = async () => {
		try {
			setLoading(true);
			const data = await partOrderService.getSupplyOrder();
			setRequirements(data);
			setError(null);
		} catch (error) {
			console.error("Error fetching part requirements:", error);
			setError("Failed to load part requirements. Please try again.");
			toast.error("Error loading part requirements");
		} finally {
			setLoading(false);
		}
	};

	const fetchVendors = async () => {
		try {
			const vendorData = await vendorService.getVendors();
			setVendors(vendorData);
		} catch (error) {
			console.error("Error fetching vendors:", error);
			toast.error("Error loading vendors");
		}
	};

	const requestSort = (key) => {
		let direction = 'ascending';
		if (sortConfig.key === key && sortConfig.direction === 'ascending') {
			direction = 'descending';
		}
		setSortConfig({ key, direction });
	};

	const getSortedRequirements = () => {
		if (!requirements.length) return [];

		let sortableItems = [...requirements];

		// Apply filtering based on search term
		if (searchTerm.trim()) {
			const normalizedSearchTerm = searchTerm.toLowerCase().trim();

			sortableItems = sortableItems.filter(item => {
				return (
					item.part_type_name.toLowerCase().includes(normalizedSearchTerm) ||
					item.vendor?.toLowerCase().includes(normalizedSearchTerm) ||
					item.type?.toLowerCase().includes(normalizedSearchTerm)
				);
			});
		}

		// Apply sorting
		if (sortConfig.key) {
			sortableItems.sort((a, b) => {
				let aValue = a[sortConfig.key];
				let bValue = b[sortConfig.key];

				// Handle special cases for nested properties or strings
				if (typeof aValue === 'string') {
					aValue = aValue.toLowerCase();
					bValue = bValue.toLowerCase();
				}

				if (aValue < bValue) {
					return sortConfig.direction === 'ascending' ? -1 : 1;
				}
				if (aValue > bValue) {
					return sortConfig.direction === 'ascending' ? 1 : -1;
				}
				return 0;
			});
		}

		return sortableItems;
	};

	const getSortIndicator = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
		}
		return '';
	};

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
	};

	const clearSearch = () => {
		setSearchTerm('');
	};

	const handleCreateOrder = async () => {
		if (!selectedVendor) {
			toast.error("Please select a vendor");
			return;
		}

		try {
			setIsCreatingOrder(true);
			// Filter requirements by the selected vendor
			const vendorRequirements = requirements.filter(
				req => req.vendor?.toLowerCase() === selectedVendor.toLowerCase()
			);

			if (vendorRequirements.length === 0) {
				toast.warning("No requirements found for the selected vendor");
				return;
			}

			const orderData = {
				vendor_id: vendors.find(v => v.name.toLowerCase() === selectedVendor.toLowerCase())?.id,
				items: vendorRequirements.map(req => ({
					part_type_id: req.part_type_id,
					quantity: req.units_to_order,
					unit_price: parseFloat(req.vendor_price) || 0
				}))
			};

			await partOrderService.createPartOrderFromRequirements(orderData);
			setOrderSuccess(true);
			setShowCreateOrderDialog(false);
			toast.success("Part order created successfully");

			// Refresh the requirements data
			fetchPartRequirements();
		} catch (error) {
			console.error("Error creating part order:", error);
			toast.error("Failed to create part order");
		} finally {
			setIsCreatingOrder(false);
		}
	};

	const sortedRequirements = getSortedRequirements();
	const uniqueVendors = [...new Set(requirements.map(req => req.vendor).filter(Boolean))];

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
				<p className="font-bold">Error</p>
				<p>{error}</p>
				<button
					onClick={fetchPartRequirements}
					className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
				>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-6">
			{/* Header */}
			<div className="mb-6">
				<div className="flex flex-wrap justify-between items-center">
					<div>
						<h1 className="text-2xl font-bold text-gray-800">Supply Requirements</h1>
						<p className="mt-1 text-gray-600">Supplies needed based on current orders and inventory levels</p>
					</div>
					<div className="mt-4 sm:mt-0 flex items-center space-x-3">
						<button
							onClick={() => setShowCreateOrderDialog(true)}
							className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
							disabled={requirements.length === 0}
						>
							Create Supply Order
						</button>
						<button
							onClick={fetchPartRequirements}
							className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none"
						>
							Refresh
						</button>
					</div>
				</div>
			</div>

			{/* Search Input */}
			<div className="mb-6">
				<div className="relative">
					<input
						type="text"
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pl-10 pr-10"
						placeholder="Search by part type, vendor, or type..."
						value={searchTerm}
						onChange={handleSearchChange}
					/>
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
						</svg>
					</div>
					{searchTerm && (
						<button
							className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
							onClick={clearSearch}
						>
							<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
							</svg>
						</button>
					)}
				</div>
				{searchTerm && (
					<div className="mt-2 text-sm text-gray-500">
						Showing {sortedRequirements.length} of {requirements.length} part requirements
					</div>
				)}
			</div>

			{!loading && !error && (
				<div className="mb-6">
					<SupplyChart />
				</div>
			)}

			{/* Requirements Table */}
			<div className="bg-white rounded-lg shadow mb-6">
				<div className="p-6">
					{sortedRequirements.length > 0 ? (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
								<tr>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('part_type_name')}
									>
										Part Type{getSortIndicator('part_type_name')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('vendor')}
									>
										Vendor{getSortIndicator('vendor')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('type')}
									>
										Type{getSortIndicator('type')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('dimension')}
									>
										Dimension{getSortIndicator('dimension')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('current_stock')}
									>
										In Stock{getSortIndicator('current_stock')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('total_quantity_needed')}
									>
										Quantity Needed{getSortIndicator('total_quantity_needed')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('units_to_order')}
									>
										Units to Order{getSortIndicator('units_to_order')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('vendor_price')}
									>
										Unit Price{getSortIndicator('vendor_price')}
									</th>
									<th
										scope="col"
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
										onClick={() => requestSort('estimated_cost')}
									>
										Estimated Cost{getSortIndicator('estimated_cost')}
									</th>
								</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
								{sortedRequirements.map((item) => (
									<tr key={item.part_type_id} className="hover:bg-gray-50">
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
											{item.part_type_name}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{item.vendor || 'N/A'}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{item.type || 'N/A'}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{item.dimension} {item.unit_type}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{parseFloat(item.current_stock).toFixed(0)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{parseFloat(item.total_quantity_needed).toFixed(0)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{item.units_to_order}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											${parseFloat(item.vendor_price || 0).toFixed(2)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											${parseFloat(item.estimated_cost || 0).toFixed(2)}
										</td>
									</tr>
								))}
								</tbody>
								<tfoot className="bg-gray-50">
								<tr>
									<td colSpan="8" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
										Total Estimated Cost:
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
										${sortedRequirements.reduce((sum, item) => sum + parseFloat(item.estimated_cost || 0), 0).toFixed(2)}
									</td>
								</tr>
								</tfoot>
							</table>
						</div>
					) : (
						 <div className="text-center py-8 text-gray-500">
							 {searchTerm ? 'No part requirements matching your search.' : 'No part requirements found.'}
						 </div>
					 )}
				</div>
			</div>

			{/* Create Order Dialog */}
			{showCreateOrderDialog && (
				<div className="fixed z-10 inset-0 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div className="fixed inset-0 transition-opacity" aria-hidden="true">
							<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
						</div>

						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Create Part Order
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Select a vendor to create a part order for all required parts from that vendor.
											</p>
										</div>

										<div className="mt-4">
											<label htmlFor="vendor" className="block text-sm font-medium text-gray-700">
												Vendor
											</label>
											<select
												id="vendor"
												name="vendor"
												className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
												value={selectedVendor || ''}
												onChange={(e) => setSelectedVendor(e.target.value)}
											>
												<option value="">Select a vendor</option>
												{uniqueVendors.map((vendor) => (
													<option key={vendor} value={vendor}>
														{vendor}
													</option>
												))}
											</select>
										</div>

										{selectedVendor && (
											<div className="mt-4">
												<h4 className="text-sm font-medium text-gray-700">Parts to Order from {selectedVendor}</h4>
												<div className="mt-2 max-h-60 overflow-y-auto">
													<ul className="divide-y divide-gray-200">
														{requirements
															.filter(req => req.vendor === selectedVendor)
															.map((req) => (
																<li key={req.part_type_id} className="py-2">
																	<div className="flex justify-between">
																		<span className="text-sm font-medium">{req.part_type_name}</span>
																		<span className="text-sm text-gray-500">{req.units_to_order} units</span>
																	</div>
																	<div className="text-xs text-gray-500">
																		Estimated cost: ${parseFloat(req.estimated_cost || 0).toFixed(2)}
																	</div>
																</li>
															))}
													</ul>
												</div>
												<div className="mt-2 pt-2 border-t border-gray-200">
													<div className="flex justify-between font-medium">
														<span>Total:</span>
														<span>
                                                            ${requirements
															.filter(req => req.vendor === selectedVendor)
															.reduce((sum, req) => sum + parseFloat(req.estimated_cost || 0), 0)
															.toFixed(2)}
                                                        </span>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={handleCreateOrder}
									disabled={isCreatingOrder || !selectedVendor}
								>
									{isCreatingOrder ? 'Creating...' : 'Create Order'}
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => setShowCreateOrderDialog(false)}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Success notification */}
			{orderSuccess && (
				<div className="fixed bottom-0 right-0 m-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg">
					<div className="flex">
						<div className="py-1">
							<svg className="h-6 w-6 text-green-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<div>
							<p className="font-bold">Order Created</p>
							<p className="text-sm">Your part order has been created successfully.</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SupplyOrder;
